<template>
  <div class="bg-white shadow-md rounded-xl p-6">
    <h2 class="text-xl font-semibold text-gray-800 border-b pb-4">Edit Podcast Profile</h2>
    <p class="text-sm text-gray-500 mt-1">Update your podcast's public branding and information.</p>
    
    <form @submit.prevent="handleSubmit" class="mt-6 space-y-6">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
        <div class="md:col-span-1">
          <label for="logoUpload" class="block text-sm font-medium text-gray-700">Podcast Icon Logo</label>
          <img :src="imagePreview || form.logoUrl" alt="Podcast Logo" class="mt-2 h-28 w-28 rounded-lg object-cover shadow-sm border">
          <input id="logoUpload" @change="handleFileChange" type="file" accept="image/png, image/jpeg" class="mt-2 text-sm w-full">
        </div>
        <div class="md:col-span-2 space-y-4">
          <div>
            <label for="podcastName" class="block text-sm font-medium text-gray-700">Podcast Name</label>
            <input v-model="form.podcastName" type="text" id="podcastName" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple">
          </div>
          <div>
            <label for="podcastSlogan" class="block text-sm font-medium text-gray-700">Podcast Slogan</label>
            <input v-model="form.podcastSlogan" type="text" id="podcastSlogan" required class="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple">
          </div>
        </div>
      </div>
      <div>
        <label for="brandColour" class="block text-sm font-medium text-gray-700">Primary Brand Colour</label>
        <div class="mt-1 flex items-center">
          <input v-model="form.brandColour" type="color" id="brandColour" class="h-10 w-10 rounded-md border-gray-300">
          <input v-model="form.brandColour" type="text" class="ml-3 block w-full max-w-xs border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:ring-brand-purple focus:border-brand-purple">
        </div>
      </div>
      <div class="pt-4 border-t">
        <button type="submit" :disabled="isProcessing" class="w-full bg-brand-purple-dark text-white py-2 px-4 rounded-md hover:opacity-90 disabled:opacity-50">
          <span v-if="isProcessing">Saving Changes...</span>
          <span v-else>Save Changes</span>
        </button>
      </div>
      <div v-if="feedbackMessage" class="mt-4 p-3 rounded-md text-sm" :class="isError ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'">
        <p v-html="feedbackMessage"></p>
      </div>
    </form>
  </div>
</template>

<script>
// --- START: DEFINITIVE FIX FOR BUILD ERROR ---
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { storage } from '@/firebase'; // Correctly import the initialized storage instance
// --- END: DEFINITIVE FIX FOR BUILD ERROR ---
import { getFunctions, httpsCallable } from 'firebase/functions';
import { mapState } from 'vuex';
import { v4 as uuidv4 } from 'uuid';

const DEFAULT_LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/castive-prod.appspot.com/o/public%2Fcastive_icon_black.png?alt=media&token=89434857-8178-4da5-94f3-3a1376f95cb0';

export default {
  name: 'PodcastProfilePanel',
  props: {
    clientData: {
      type: Object,
      default: () => null
    }
  },
  data() {
    return {
      form: {
        podcastName: '',
        podcastSlogan: 'Cast Smarter',
        brandColour: '#E7643B',
        logoUrl: DEFAULT_LOGO_URL,
      },
      newLogoFile: null,
      imagePreview: null,
      isProcessing: false,
      feedbackMessage: '',
      isError: false,
    };
  },
  computed: {
    ...mapState(['clientId']),
  },
  watch: {
    clientData: {
      handler(newData) {
        if (newData) {
          this.form.podcastName = newData.podcastName || '';
          this.form.podcastSlogan = newData.podcastSlogan || 'Cast Smarter';
          this.form.brandColour = newData.brandColour || '#E7643B';
          this.form.logoUrl = newData.logoUrl || DEFAULT_LOGO_URL;
        }
      },
      immediate: true,
      deep: true,
    },
  },
  methods: {
    handleFileChange(event) {
      const file = event.target.files[0];
      if (file) {
        this.newLogoFile = file;
        this.imagePreview = URL.createObjectURL(file);
      }
    },
    async uploadLogo() {
      if (!this.newLogoFile || !this.clientId) {
        return this.form.logoUrl;
      }
      
      const fileExtension = this.newLogoFile.name.split('.').pop();
      const uniqueFileName = `${uuidv4()}.${fileExtension}`;
      // --- DEFINITIVE FIX: Use the imported 'storage' instance ---
      const storageRef = ref(storage, `client-logos/${this.clientId}/${uniqueFileName}`);

      try {
        await uploadBytes(storageRef, this.newLogoFile);
        const downloadUrl = await getDownloadURL(storageRef);
        return downloadUrl;
      } catch (storageError) {
        console.error("DIRECT FIREBASE STORAGE ERROR:", storageError);
        throw storageError;
      }
    },
    async handleSubmit() {
      this.isProcessing = true;
      this.feedbackMessage = '';
      this.isError = false;
      try {
        const newLogoUrl = await this.uploadLogo();
        const payload = {
          podcastName: this.form.podcastName,
          podcastSlogan: this.form.podcastSlogan,
          brandColour: this.form.brandColour,
          logoUrl: newLogoUrl,
        };
        const functions = getFunctions(undefined, 'europe-west2');
        const updatePodcastProfile = httpsCallable(functions, 'updatePodcastProfile');
        const response = await updatePodcastProfile(payload);
        this.feedbackMessage = response.data.message;
        this.newLogoFile = null;
        this.$emit('profile-updated');
      } catch (error) {
        console.error("Error updating podcast profile (Full Error Object):", error);
        let errorMessage = error?.message || 'An unknown error occurred. Check the console.';
        this.feedbackMessage = `<b>Error:</b> ${errorMessage}`;
        this.isError = true;
      } finally {
        this.isProcessing = false;
      }
    },
  },
};
</script>